/**
 * Hauptansprechperson - the G4 card on the Kunde Übersicht.
 *
 * WHAT G4 ASKS FOR (build plan PART 2B, G4 + the meeting-2 refinement):
 *   "On the customer page Overview: show ALL employees of that company and WHO the
 *    Hauptansprechperson is. Consider a top summary showing BOTH the client-side main contact
 *    AND the broker-side main contact (Betreuer) for that customer."
 *
 * So this card does three things: list the people, mark the one who is the main contact, and
 * let any user who can edit the customer change it (G3 - no special role).
 *
 * TWO HONESTY DECISIONS worth keeping:
 *
 * 1. The card says WHY somebody is the main contact. The api returns `source`, and
 *    'default_first' is rendered differently from 'explicit', because "this is the first
 *    person you added" and "somebody chose this person" are different facts and the broker
 *    should not have to guess which one they are looking at.
 *
 * 2. The broker-side Betreuer half of G4 is NOT rendered, and it is not an oversight. There
 *    is no customer-level Betreuer field in the schema: `broker_owner` lives on POLICY, so a
 *    customer with policies split across two brokers has no single answer. Inventing one
 *    (most-common owner, first policy, whatever) would put a name on this card that no data
 *    supports. It is named in the output report and in pending_tests instead.
 *
 * Firma-only: a natural-person customer is their own contact and has no employee list.
 *
 * P2-8 (spec-part2-stefan-delta.md §3.2/§3.4): surfaces the Hauptansprechperson's generated
 * Briefanrede (`person.briefanrede`, e.g. "Sehr geehrte Frau Erste") — the api already exposes
 * it on `CompanyEmployeeDto` (P2-7, `main-contact.service.ts`); this card is the one read
 * surface that resolves "for a firma customer, the Briefanrede uses the Hauptansprechperson",
 * so it renders only on the marked main-contact row, not on every employee.
 *
 * [Source: apps/web/src/features/entity-detail/components/FirmenDomains.tsx] (card + async-state precedent)
 * [Source: apps/api/src/modules/firms/main-contact.service.ts] (briefanrede on CompanyEmployeeDto)
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Star, UserPlus, UserRound } from 'lucide-react';
import { apiClient } from '../../../lib/api-client';
import type { MainContactResponse } from '../../../lib/api-client';

export interface HauptansprechpersonProps {
  firmaId: string;
  /** Holds `crm.write`: may change who the Hauptansprechperson is (G3). */
  canEdit: boolean;
}

export function Hauptansprechperson({ firmaId, canEdit }: HauptansprechpersonProps) {
  const [data, setData] = useState<MainContactResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  // The "Person hinzufügen" inline form (the G-3 gap: the card could re-designate an existing
  // person but never add one). Local UI state only — the api is the authority on what persists.
  const [adding, setAdding] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.mainContact
      .get(firmaId)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Die Ansprechpersonen konnten nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmaId]);

  const setMain = async (personId: string) => {
    setSaving(personId);
    setError(null);
    try {
      setData(await apiClient.mainContact.set(firmaId, personId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Die Änderung konnte nicht gespeichert werden.');
    } finally {
      setSaving(null);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
  };

  const closeForm = () => {
    setAdding(false);
    resetForm();
  };

  const addContact = async (e: React.FormEvent) => {
    e.preventDefault();
    const nachname = lastName.trim();
    if (nachname === '' || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const vorname = firstName.trim();
      const mail = email.trim();
      // The card refreshes by taking the api's returned answer (same pattern as setMain), so a
      // rejected add can never look accepted. If it was the firm's first contact, the server has
      // already marked it Hauptansprechperson and the response reflects that.
      setData(
        await apiClient.mainContact.addContact(firmaId, {
          lastName: nachname,
          ...(vorname !== '' ? { firstName: vorname } : {}),
          ...(mail !== '' ? { email: mail } : {}),
        }),
      );
      closeForm();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Die Person konnte nicht erfasst werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-lg border bg-card" aria-label="Ansprechpersonen">
      <header className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Ansprechpersonen</h3>
        <p className="text-xs text-muted-foreground">
          Die Hauptansprechperson erhält standardmässig die Mandatsunterlagen.
        </p>
      </header>

      <div className="px-4 py-3">
        {loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Wird geladen …
          </p>
        )}

        {error !== null && (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="size-4 shrink-0" />
            {error}
          </p>
        )}

        {!loading && data !== null && data.employees.length === 0 && (
          // Honest empty state: no invented contact, and it says what to do about it.
          <p className="text-sm text-muted-foreground">
            Für diesen Kunden sind noch keine Personen erfasst. Die erste erfasste Person wird
            automatisch zur Hauptansprechperson.
          </p>
        )}

        {!loading && data !== null && data.employees.length > 0 && (
          <>
            {data.source === 'default_first' && (
              // "This is the first person you added" is a different fact from "somebody chose
              // this person", and the broker should not have to guess which they are seeing.
              <p className="mb-2 text-xs text-muted-foreground">
                Noch nicht festgelegt: die zuerst erfasste Person gilt als Hauptansprechperson.
              </p>
            )}
            <ul className="flex flex-col gap-1">
              {data.employees.map((e) => (
                <li
                  key={e.personId}
                  className="flex items-center gap-2 rounded px-2 py-1.5 text-sm"
                >
                  <UserRound className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{e.displayName}</span>
                    {e.role !== null && (
                      <span className="text-muted-foreground"> · {e.role}</span>
                    )}
                    {e.email !== null && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {e.email}
                      </span>
                    )}
                    {/* P2-8: the Hauptansprechperson's generated Briefanrede — null until the
                        first auto-fill (§3.4), so an unset one renders nothing rather than a
                        confusing "Briefanrede: -". */}
                    {e.isMainContact && e.briefanrede !== null && (
                      <span
                        className="block truncate text-xs text-muted-foreground"
                        title="Generierte Briefanrede"
                      >
                        Briefanrede: {e.briefanrede}
                      </span>
                    )}
                  </span>
                  {e.isMainContact ? (
                    <span
                      className="flex shrink-0 items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs"
                      title="Hauptansprechperson"
                    >
                      <Star className="size-3" aria-hidden />
                      Hauptansprechperson
                    </span>
                  ) : (
                    canEdit && (
                      <button
                        type="button"
                        className="shrink-0 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                        disabled={saving !== null}
                        onClick={() => void setMain(e.personId)}
                      >
                        {saving === e.personId ? 'Wird gespeichert …' : 'Als Hauptansprechperson'}
                      </button>
                    )
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* G-3 gap closed: add a NEW contact person (auto-set as Hauptansprechperson server-side
            iff it is the firm's first). Only for a user who can edit the customer (crm.write). */}
        {canEdit && !loading && (
          <div className="mt-3 border-t pt-3">
            {!adding ? (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
                onClick={() => {
                  setError(null);
                  setAdding(true);
                }}
              >
                <UserPlus className="size-3.5" aria-hidden />
                Person hinzufügen
              </button>
            ) : (
              <form onSubmit={(e) => void addContact(e)} className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <label className="sr-only" htmlFor="neue-person-vorname">
                    Vorname
                  </label>
                  <input
                    id="neue-person-vorname"
                    type="text"
                    value={firstName}
                    onChange={(ev) => setFirstName(ev.target.value)}
                    placeholder="Vorname"
                    disabled={submitting}
                    className="h-9 flex-1 rounded-md border border-border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <label className="sr-only" htmlFor="neue-person-nachname">
                    Nachname
                  </label>
                  <input
                    id="neue-person-nachname"
                    type="text"
                    value={lastName}
                    onChange={(ev) => setLastName(ev.target.value)}
                    placeholder="Nachname"
                    required
                    disabled={submitting}
                    className="h-9 flex-1 rounded-md border border-border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <label className="sr-only" htmlFor="neue-person-email">
                  E-Mail
                </label>
                <input
                  id="neue-person-email"
                  type="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder="E-Mail (optional)"
                  disabled={submitting}
                  className="h-9 rounded-md border border-border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={lastName.trim() === '' || submitting}
                    className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {submitting ? 'Wird erfasst …' : 'Hinzufügen'}
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={closeForm}
                    className="rounded px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
